import { useRouter } from 'next/router'
import { useEffect, useRef, useState } from 'react'
import { useQueryClient } from 'react-query'

import { TabPanel, TabPanels, Tabs } from '@wartek-id/tabs'
import { Icon } from '@wartek-id/icon-v2'
import { Shimmer } from '@wartek-id/shimmer'
import { useToast } from '@wartek-id/toast'

import { Header } from 'app/KinerjaShared/components/Header'
import { tabNamesV2 } from 'app/KinerjaKSPage/constants'
import { SKP_QUERY_KEY, useAPIUpsertSKP } from 'api/kinerja/useAPIKinerjaKS'
import DialogSaveDraft from 'app/KinerjaShared/components/DialogSaveDraft'
import PanelSummary from 'app/KinerjaKSPage/components/PanelSummary'
import CustomTabList from 'app/KinerjaKSPage/components/CustomTabList'
import Tab1 from 'app/KinerjaKSPage/pages/Perencanaan/Tab1'
import Tab2 from 'app/KinerjaKSPage/pages/Perencanaan/Tab2'
import Tab3 from 'app/KinerjaKSPage/pages/Perencanaan/Tab3'
import TabRangkuman from 'app/KinerjaKSPage/pages/Perencanaan/TabRangkuman'
import useSchoolContext from 'app/KinerjaKSPage/hooks/useSchoolContext'
import { isUserHasPltRole } from 'app/KinerjaKSPage/utils/profile'
import useDemoContext from 'app/KinerjaPage/hooks/useDemoContext'
import usePeriodContext from 'app/KinerjaShared/hooks/usePeriodContext'
import Tab2Year2025Onwards from 'app/KinerjaKSPage/pages/Perencanaan/Tab2Year2025Onwards'
import useToggleSdkContext from 'utils/useToogleSdk'
import { usePengelolaanKinerjaBaseUrl } from 'app/KinerjaShared/hooks/usePengelolaanKinerjaBaseUrl'

import type { KinerjaKs, UpdateSKPKSPayloadWithoutNpsn } from 'types/kinerja-ks'
import type { SKPTabHandler } from 'types/kinerja'

interface KinerjaPerencanaanAppProps {
  skp: KinerjaKs
  isSKPFetched: boolean
}

enum TabIndex {
  KEPEMIMPINAN_SATUAN_PENDIDIKAN = 0,
  PENGEMBANGAN_KOMPENTESI = 1,
  PERILAKU_KERJA = 2,
  RANGKUMAN = 3,
}

function KinerjaPerencanaanApp({
  skp,
  isSKPFetched,
}: KinerjaPerencanaanAppProps) {
  const toggles = useToggleSdkContext()
  const toast = useToast()
  const router = useRouter()
  const queryClient = useQueryClient()
  const schoolCtx = useSchoolContext()
  const { isDemoMode } = useDemoContext()
  const { periodYear } = usePeriodContext()

  const [activeTabIndex, setActiveTabIndex] = useState(
    TabIndex.KEPEMIMPINAN_SATUAN_PENDIDIKAN
  )

  const [isShowDialogSaveDraft, setIsShowDialogSaveDraft] = useState(false)

  const { mutateAsync: mutateSKP, isLoading: isUpdatingSKP } = useAPIUpsertSKP({
    isDemoMode,
  })

  const isSPNFEnabled = toggles?.['kinerja-spnf-enabled'] ?? false
  const isSKPApproved = skp?.status === 'APPROVED'

  const baseUrl = usePengelolaanKinerjaBaseUrl()

  useEffect(() => {
    if (skp?.status === 'NOT_FOUND') {
      router.push(`${baseUrl}/konfirmasi-profil`)
    } else if (skp?.status === 'READY') {
      router.push(baseUrl)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skp])

  const goToHome = () => {
    router.push(baseUrl)
  }

  const handleClickBack = () => {
    if (skp?.status === 'DRAFT' && activeTabIndex !== TabIndex.RANGKUMAN) {
      setIsShowDialogSaveDraft(true)
    } else {
      goToHome()
    }
  }

  const tabHandlerRef = useRef<SKPTabHandler>()

  const handleError = (error: any) => {
    console.error(error)

    const message =
      error?.originalError?.response?.data?.error?.message ?? error.toString()

    toast({
      message: `Gagal mengubah rencana hasil kerja: ${message}`,
    })
  }

  // Field `npsn` can be safely ignored since it will be automatically added to the payload.
  const updateSKP = async (data: UpdateSKPKSPayloadWithoutNpsn) => {
    await mutateSKP({
      ...data,
      npsn: schoolCtx.currentNpsn,
      ...(isSPNFEnabled && schoolCtx.selectedSchool
        ? { role: schoolCtx.selectedSchool.role }
        : {}),
    })
    await queryClient.invalidateQueries(SKP_QUERY_KEY)
  }

  // Field `npsn` can be safely ignored since it will be automatically added to the payload.
  const handleSaveDraft = async (data?: UpdateSKPKSPayloadWithoutNpsn) => {
    try {
      // Only update if there is data
      if (!!data) {
        await updateSKP(data)
      }

      if (isDemoMode) {
        // on demo mode, reset to first tab
        setActiveTabIndex(TabIndex.KEPEMIMPINAN_SATUAN_PENDIDIKAN)
      } else {
        goToHome()
      }

      toast({
        message: 'Berhasil simpan draf!',
        icon: <Icon color="inverse">check</Icon>,
      })
    } catch (error) {
      handleError(error)
    }
  }

  // Field `npsn` can be safely ignored since it will be automatically added to the payload.
  const handleClickNext = async (
    data: UpdateSKPKSPayloadWithoutNpsn,
    tabIndex: number
  ) => {
    try {
      await updateSKP(data)

      setActiveTabIndex(tabIndex)
    } catch (error) {
      handleError(error)
    }
  }

  return (
    <main className="py-14 px-6 max-w-7xl mx-auto">
      <Header
        title="Perencanaan Kinerja"
        onBack={handleClickBack}
        rightNode={
          schoolCtx.isProfileFetched &&
          isUserHasPltRole(schoolCtx.profile) && (
            <PanelSummary
              items={[
                ...(isUserHasPltRole(schoolCtx.profile) || false
                  ? [
                      {
                        label: schoolCtx?.selectedSchoolName,
                        value: null,
                      },
                    ]
                  : []),
              ]}
            />
          )
        }
      ></Header>

      {!isSKPFetched ? (
        <Shimmer data-testid="loading-spinner-skp" />
      ) : (
        <div className="bg-white rounded-md mt-8">
          <Tabs onChange={setActiveTabIndex} selectedIndex={activeTabIndex}>
            <CustomTabList
              activeTabIndex={activeTabIndex}
              tabs={!isSKPApproved ? tabNamesV2 : tabNamesV2.slice(0, 4)}
              disableInactiveTab
            />
            <TabPanels>
              <TabPanel>
                <Tab1
                  ref={tabHandlerRef}
                  skp={skp}
                  onClickSave={handleSaveDraft}
                  isSubmitting={isUpdatingSKP}
                  onClickNext={(data) =>
                    handleClickNext(data, TabIndex.PENGEMBANGAN_KOMPENTESI)
                  }
                />
              </TabPanel>
              <TabPanel>
                {periodYear === 2024 && (
                  <Tab2
                    ref={tabHandlerRef}
                    skp={skp}
                    onClickSave={handleSaveDraft}
                    onClickNext={(data) =>
                      handleClickNext(data, TabIndex.PERILAKU_KERJA)
                    }
                    isSubmitting={isUpdatingSKP}
                  />
                )}

                {periodYear >= 2025 && (
                  <Tab2Year2025Onwards
                    ref={tabHandlerRef}
                    skp={skp}
                    onClickSave={handleSaveDraft}
                    onClickNext={(data) =>
                      handleClickNext(data, TabIndex.PERILAKU_KERJA)
                    }
                    isSubmitting={isUpdatingSKP}
                  />
                )}
              </TabPanel>
              <TabPanel>
                <Tab3
                  ref={tabHandlerRef}
                  skp={skp}
                  onClickSave={handleSaveDraft}
                  isLoading={isUpdatingSKP}
                  onClickNext={(data) =>
                    handleClickNext(data, TabIndex.RANGKUMAN)
                  }
                />
              </TabPanel>
              <TabPanel>
                <TabRangkuman
                  hideDetails
                  ref={tabHandlerRef}
                  skp={skp}
                  onClickSave={handleSaveDraft}
                  isSubmitting={isUpdatingSKP}
                  onRequestChange={() =>
                    setActiveTabIndex(TabIndex.KEPEMIMPINAN_SATUAN_PENDIDIKAN)
                  }
                  onClickBackToHome={goToHome}
                />
              </TabPanel>
            </TabPanels>
          </Tabs>
        </div>
      )}

      <DialogSaveDraft
        data-testid="dialog-save-draft"
        isOpen={isShowDialogSaveDraft}
        isLoading={isUpdatingSKP}
        onClose={() => setIsShowDialogSaveDraft(false)}
        onClickCancel={() => setIsShowDialogSaveDraft(false)}
        onClickConfirm={() => {
          tabHandlerRef.current?.saveDraft()
        }}
      />
    </main>
  )
}

export default KinerjaPerencanaanApp
